import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { 
  FaBars, 
  FaTimes, 
  FaPhone, 
  FaEnvelope,
  FaMapMarkerAlt,
  FaInstagram
} from 'react-icons/fa';
import {
  CONTACT_PHONE_DISPLAY,
  CONTACT_PHONE_TEL,
  CONTACT_EMAIL,
  CONTACT_MAILTO,
  CONTACT_INSTAGRAM_URL
} from '../config/contact';

const HeaderContainer = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: ${props => props.scrolled ? 'rgba(255, 255, 255, 0.95)' : 'transparent'};
  backdrop-filter: ${props => props.scrolled ? 'blur(10px)' : 'none'};
  transition: var(--transition);
  box-shadow: ${props => props.scrolled ? 'var(--shadow)' : 'none'};
`;

const TopBar = styled.div`
  background: var(--primary-color);
  color: var(--white-color);
  padding: 8px 0;
  font-size: 14px;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const TopBarContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  gap: 16px;
`;

const TopBarLeft = styled.div`
  display: flex;
  gap: 20px;
  align-items: center;
`;

const TopBarItem = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const TopBarLink = styled.a`
  color: inherit;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const InstagramTopLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: inherit;
  text-decoration: none;
  font-weight: 600;
  flex-shrink: 0;
  transition: var(--transition);

  &:hover {
    opacity: 0.92;
    transform: scale(1.04);
  }

  svg {
    font-size: 22px;
  }
`;

const MobileInstagramLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  font-size: 20px;
  font-weight: 600;
  color: var(--primary-color);
  text-decoration: none;
  margin-top: 8px;

  &:hover {
    text-decoration: underline;
  }
`;

const MainHeader = styled.div`
  padding: 15px 0;
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const LogoWrap = styled(Link)`
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
`;

const LogoImage = styled.img`
  width: 40px;
  height: 40px;
  object-fit: contain;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 34px;
    height: 34px;
  }
`;

const LogoText = styled.span`
  font-size: 28px;
  font-weight: 700;
  color: var(--primary-color);
  text-decoration: none;
  
  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const Nav = styled.nav`
  display: flex;
  gap: 30px;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const NavLink = styled(Link)`
  color: var(--secondary-color);
  font-weight: 500;
  text-decoration: none;
  transition: var(--transition);
  position: relative;
  
  &:hover {
    color: var(--primary-color);
  }
  
  &.active {
    color: var(--primary-color);
    
    &::after {
      content: '';
      position: absolute;
      bottom: -5px;
      left: 0;
      width: 100%;
      height: 2px;
      background: var(--primary-color);
    }
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  font-size: 24px;
  color: var(--secondary-color);
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const MobileMenu = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--white-color);
  z-index: 1001;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 30px;
`;

const MobileNavLink = styled(Link)`
  font-size: 24px;
  font-weight: 600;
  color: var(--secondary-color);
  text-decoration: none;
  transition: var(--transition);
  
  &:hover {
    color: var(--primary-color);
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  font-size: 24px;
  color: var(--secondary-color);
`;

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { path: '/', label: 'Anasayfa' },
    { path: '/hakkimizda', label: 'Hakkımızda' },
    { path: '/hizmetler', label: 'Hizmetler' },
    { path: '/hayvanlar', label: 'Hayvanlar' },
    { path: '/iletisim', label: 'İletişim' }
  ];

  return (
    <HeaderContainer scrolled={scrolled}>
      <TopBar>
        <TopBarContent>
          <TopBarLeft>
            <TopBarItem>
              <FaPhone />
              <TopBarLink href={CONTACT_PHONE_TEL}>{CONTACT_PHONE_DISPLAY}</TopBarLink>
            </TopBarItem>
            <TopBarItem>
              <FaEnvelope />
              <TopBarLink href={CONTACT_MAILTO}>{CONTACT_EMAIL}</TopBarLink>
            </TopBarItem>
            <TopBarItem>
              <FaMapMarkerAlt />
              <span>Burdur, Türkiye</span>
            </TopBarItem>
          </TopBarLeft>
          <InstagramTopLink
            href={CONTACT_INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Instagram — Burdur Sultanlar Besi Çiftliği"
          >
            <FaInstagram aria-hidden />
            <span>Instagram</span>
          </InstagramTopLink>
        </TopBarContent>
      </TopBar>
      
      <MainHeader>
        <HeaderContent>
          <LogoWrap to="/">
            <LogoImage src="/images/logo/sultanlar-logo.png" alt="Sultanlar Besicilik Logo" />
            <LogoText>SULTANLAR BESİCİLİK</LogoText>
          </LogoWrap>
          
          <Nav>
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={location.pathname === item.path ? 'active' : ''}
              >
                {item.label}
              </NavLink>
            ))}
          </Nav>
          
          <MobileMenuButton onClick={() => setMobileMenuOpen(true)}>
            <FaBars />
          </MobileMenuButton>
        </HeaderContent>
      </MainHeader>

      {mobileMenuOpen && (
        <MobileMenu
          initial={{ opacity: 0, x: '100%' }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: '100%' }}
          transition={{ duration: 0.3 }}
        >
          <CloseButton onClick={() => setMobileMenuOpen(false)}>
            <FaTimes />
          </CloseButton>
          
          {navItems.map((item) => (
            <MobileNavLink
              key={item.path}
              to={item.path}
              onClick={() => setMobileMenuOpen(false)}
            >
              {item.label}
            </MobileNavLink>
          ))}
          <MobileInstagramLink
            href={CONTACT_INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setMobileMenuOpen(false)}
          >
            <FaInstagram size={28} aria-hidden />
            Instagram
          </MobileInstagramLink>
        </MobileMenu>
      )}
    </HeaderContainer>
  );
};

export default Header; 